import { useCallback, useMemo, useState } from "react";
import client from "libs/client";
import queryClient from "libs/queryClient";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import { useDiscoverGamesBulkSelect } from "../../../../datagrid-configs/discoverGames";
import type { DiscoverGamesBulkUpdatePayload } from "../DiscoverGamesBulkUpdateModal";
import { buildBulkPatchRequestForId } from "../discoverGamesBulkApply";
import { adaptEligibilityResponse } from "../helpers/adaptEligibilityResponse";

export function useDiscoverGamesBatchActions() {
	const { addErrorToast, addSuccessToast } = useToast();
	const { selectedIds, clear } = useDiscoverGamesBulkSelect();
	const selectedCount = selectedIds.size;
	const [showBulkModal, setShowBulkModal] = useState(false);
	const [isBulkApplying, setIsBulkApplying] = useState(false);

	const selectedIdsArray = useMemo(
		() => Array.from(selectedIds).sort((a, b) => a - b),
		[selectedIds],
	);
	const shouldFetchEligibility = showBulkModal && selectedIdsArray.length > 0;
	const { data: bulkEligibilityResp, isLoading: isEligibilityLoading } =
		client.getDiscoverGamesBulkEligibility.useQuery(
			{ ids: selectedIdsArray },
			{
				enabled: shouldFetchEligibility,
				queryKey: ["getDiscoverGamesBulkEligibility", selectedIdsArray],
			},
		);
	const eligibility = useMemo(
		() => adaptEligibilityResponse(selectedCount, isEligibilityLoading, shouldFetchEligibility ? bulkEligibilityResp?.data : undefined),
		[bulkEligibilityResp, isEligibilityLoading, selectedCount, shouldFetchEligibility],
	);

	const bulkPatchDiscoverGamesMutation = client.postDiscoverGamesBulkPatch.useMutation();

	const handleBulkApply = useCallback(
		async (payload: DiscoverGamesBulkUpdatePayload) => {
			const ids = Array.from(selectedIds);
			if (ids.length === 0) {
				return;
			}

			const needsEligibilityData =
				payload.applyAssignee || payload.applyPriority || payload.applyGameStatus;
			if (needsEligibilityData && !eligibility.hasData) {
				return;
			}

			let wouldUpdate = 0;
			for (const id of ids) {
				if (buildBulkPatchRequestForId(id, payload, eligibility) !== null) {
					wouldUpdate += 1;
				}
			}
			if (wouldUpdate === 0) {
				addErrorToast(_("discover.gamesTable.bulkUpdate.noneEligibleToast"));
				return;
			}

			setIsBulkApplying(true);
			try {
				const result = await bulkPatchDiscoverGamesMutation.mutateAsync({
					body: {
						ids,
						applyClubManager: payload.applyClubManager,
						clubManagerId: payload.clubManagerId,
						applyContentManager: payload.applyContentManager,
						contentManagerId: payload.contentManagerId,
						applyAssignee: payload.applyAssignee,
						assigneeId: payload.assigneeId,
						applyPriority: payload.applyPriority,
						priority: payload.priority,
						applyGameStatus: payload.applyGameStatus,
						gameStatus: payload.gameStatus,
					},
				});

				const data = result?.data;
				if (!data) {
					addErrorToast(_("discover.gamesTable.bulkUpdate.allFailed"));
					return;
				}

				const updated = data.updated;
				const skippedRows = data.skipped;
				const totalSelected = ids.length;

				if (updated > 0) {
					await queryClient.invalidateQueries(["getDiscoverGamesDynamic"]);
					// Keep manager/assignee filter dropdowns in sync after a bulk update too.
					await queryClient.invalidateQueries(["getDiscoverGamesV2FilterOptions"]);
				}

				const skippedPerField = data.skippedPerField ?? {};
				const skippedFieldsSummary = Object.entries(skippedPerField)
					.filter(([, count]) => count > 0)
					.map(([field, count]) => `${field}:${count}`)
					.join(", ");

				if (skippedRows === 0) {
					addSuccessToast(_("message.status.update.success"));
					clear();
					setShowBulkModal(false);
					return;
				}

				addSuccessToast(
					_("discover.gamesTable.bulkUpdate.partialSkipped", {
						updated: updated,
						skipped: skippedRows,
						total: totalSelected,
						fields: skippedFieldsSummary,
					}),
				);
				clear();
				setShowBulkModal(false);
			} catch (error) {
				const message =
					error instanceof Error && error.message
						? error.message
						: _("discover.gamesTable.bulkUpdate.allFailed");
				addErrorToast(message);
			} finally {
				setIsBulkApplying(false);
			}
		},
		[selectedIds, eligibility, bulkPatchDiscoverGamesMutation, queryClient, addSuccessToast, addErrorToast, clear],
	);

	const bulkUpdateTooltip =
		selectedCount === 0
			? _("discover.gamesTable.bulkUpdate.selectRowsFirstHint")
			: _("discover.gamesTable.bulkUpdate.openTitle");

	const clearBulkOnQueryChange = useCallback(() => {
		clear();
		setShowBulkModal(false);
	}, [clear]);

	return {
		selectedIds,
		selectedCount,
		showBulkModal,
		setShowBulkModal,
		isBulkApplying,
		eligibility,
		handleBulkApply,
		bulkUpdateTooltip,
		clearBulkOnQueryChange,
	};
}
